import { fieldSurface } from '../field-focus'
import { labelContent } from './labelContent'
import { useId, useState, useRef, useEffect } from 'react'
import { ChevronDown, Search, Check, X } from 'lucide-react'

export interface CheckboxDropdownOption {
  value: string
  label: string
}

interface CheckboxDropdownProps {
  label?: string
  /** Accessible name when there is no visible label. Falls back to the placeholder. */
  ariaLabel?: string
  /** Id of an element that describes the control, such as a hint under it. */
  ariaDescribedBy?: string
  size?: 'large' | 'small'
  options: CheckboxDropdownOption[]
  value: string[]
  onChange: (value: string[]) => void
  placeholder?: string
  error?: string
  disabled?: boolean
  className?: string
}

const SIZE: Record<'large' | 'small', { height: number; px: number }> = {
  large: { height: 40, px: 12 },
  small: { height: 32, px: 10 },
}

export function CheckboxDropdown({
  label,
  size = 'large',
  options,
  value,
  onChange,
  placeholder = 'Select…',
  error,
  disabled = false,
  className = '',
  ariaLabel,
  ariaDescribedBy,
}: CheckboxDropdownProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [searchFocused, setSearchFocused] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const id = useId()
  const labelId = `${id}-label`

  useEffect(() => {
    function onMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
        setQuery('')
      }
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [])

  function toggle(optValue: string) {
    onChange(
      value.includes(optValue)
        ? value.filter(v => v !== optValue)
        : [...value, optValue]
    )
  }

  const filtered = query.trim()
    ? options.filter(o => o.label.toLowerCase().includes(query.toLowerCase()))
    : options

  const triggerLabel =
    value.length === 0 ? null
    : value.length === 1 ? options.find(o => o.value === value[0])?.label
    : `${value.length} selected`

  const { borderColor, backgroundColor } = fieldSurface({ focused: open, error: Boolean(error) })
  const { height, px } = SIZE[size]

  return (
    <div className={`flex flex-col ${className}`} style={{ gap: 8 }}>
      {label && (
        <label id={labelId} htmlFor={id} className="font-mulish text-text-support" style={{ fontSize: 12, lineHeight: '16px' }}>
          {labelContent(label)}
        </label>
      )}

      <div ref={containerRef} className="relative">
        {/* Trigger */}
        <button
          type="button"
          id={id}
          aria-expanded={open}
          aria-haspopup="listbox"
          {...(label ? { 'aria-labelledby': labelId } : { 'aria-label': ariaLabel ?? placeholder })}
          {...(ariaDescribedBy ? { 'aria-describedby': ariaDescribedBy } : {})}
          disabled={disabled}
          onClick={() => !disabled && setOpen(o => !o)}
          className="w-full flex items-center justify-between font-jakarta"
          style={{
            height,
            backgroundColor,
            border: `0.5px solid ${borderColor}`,
            paddingLeft: px,
            paddingRight: px,
            opacity: disabled ? 0.6 : 1,
            cursor: disabled ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.15s ease',
          }}
        >
          <span
            className={triggerLabel ? 'text-text-input' : 'text-text-description'}
            style={{ fontSize: 12, lineHeight: '16px', letterSpacing: '-0.12px' }}
          >
            {triggerLabel ?? placeholder}
          </span>
          <ChevronDown
            size={14}
            className={`flex-shrink-0 text-text-support transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
          />
        </button>

        {/* Dropdown panel */}
        {open && (
          <div
            className="absolute left-0 right-0 z-50 flex flex-col"
            style={{
              top: height + 4,
              backgroundColor: '#1A1D21',
              border: '0.5px solid #4B5563',
              paddingTop: 8,
              paddingBottom: 8,
              gap: 8,
            }}
          >
            {/* Search bar */}
            <div style={{ paddingLeft: 10, paddingRight: 10 }}>
              <div
                className="flex items-center"
                style={{
                  backgroundColor: searchFocused ? '#2D3036' : '#1F2937',
                  border: '0.5px solid #4B5563',
                  padding: '8px 10px',
                  gap: 10,
                  transition: 'background-color 0.15s ease',
                }}
              >
                <Search size={12} className="flex-shrink-0 text-text-support" />
                <input
                  type="text"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setSearchFocused(false)}
                  placeholder="Search…"
                  className="flex-1 min-w-0 bg-transparent font-mulish text-text-input placeholder:text-text-support outline-none"
                  style={{ fontSize: 12, lineHeight: '16px', letterSpacing: '-0.12px', caretColor: '#FFFFFF' }}
                />
                {query && (
                  <button
                    type="button"
                    onMouseDown={e => { e.preventDefault(); setQuery('') }}
                    className="flex-shrink-0 flex items-center justify-center hover:opacity-70 transition-opacity"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                  >
                    <X size={12} className="text-text-support" />
                  </button>
                )}
              </div>
            </div>

            {/* Options — 2-column wrap */}
            <div
              className="flex flex-wrap overflow-y-auto"
              style={{ maxHeight: 200 }}
            >
              {filtered.length === 0 ? (
                <p className="font-mulish text-text-support" style={{ fontSize: 12, padding: '8px 10px' }}>
                  No results
                </p>
              ) : filtered.map(opt => {
                const checked = value.includes(opt.value)
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => toggle(opt.value)}
                    className="flex items-center text-left w-1/2"
                    style={{ padding: '8px 10px', gap: 10, background: 'none', border: 'none', cursor: 'pointer' }}
                  >
                    {/* Checkbox */}
                    <span
                      className="flex-shrink-0 flex items-center justify-center"
                      style={{
                        width: 16,
                        height: 16,
                        backgroundColor: '#1A1D21',
                        border: `0.5px solid ${checked ? '#ABACAE' : '#4B5563'}`,
                      }}
                    >
                      {checked && <Check size={10} className="text-white" strokeWidth={2.5} />}
                    </span>
                    <span
                      className="font-jakarta text-white"
                      style={{ fontSize: 12, lineHeight: '16px', letterSpacing: '-0.12px' }}
                    >
                      {opt.label}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
        )}
      </div>

      {error && (
        <span className="font-mulish" style={{ fontSize: 10, lineHeight: '12px', color: '#F05042' }}>
          {error}
        </span>
      )}
    </div>
  )
}
